import { z } from 'zod'

import { DateStringSchema, type ZodInfer } from '@mntn-dev/utility-types'

import { ChargeSchema } from '../billing/charge.ts'
import { RefundSchema } from '../billing/refund.ts'
import { PaymentSchema } from '../payment/payment.ts'

export const PaymentTransactionSchema = z.object({
  transactionKind: z.literal('payment'),
  description: z.string().optional(),
  createdTime: DateStringSchema,
  payment: PaymentSchema,
})
export type PaymentTransaction = ZodInfer<typeof PaymentTransactionSchema>

export const ChargeTransactionSchema = z.object({
  transactionKind: z.literal('charge'),
  description: z.string().optional(),
  createdTime: DateStringSchema,
  charge: ChargeSchema,
})

export type ChargeTransaction = ZodInfer<typeof ChargeTransactionSchema>

export const RefundTransactionSchema = z.object({
  transactionKind: z.literal('refund'),
  description: z.string().optional(),
  refund: RefundSchema,
  createdTime: DateStringSchema,
  projectName: z.string(),
})

export type RefundTransaction = ZodInfer<typeof RefundTransactionSchema>

export const TransactionSchema = z.discriminatedUnion('transactionKind', [
  PaymentTransactionSchema,
  ChargeTransactionSchema,
  RefundTransactionSchema,
])

export type Transaction = ZodInfer<typeof TransactionSchema>

export function transactionIsCharge(
  transaction?: Transaction
): transaction is Transaction & { transactionKind: 'charge' } {
  return transaction?.transactionKind === 'charge'
}

export function transactionIsPayment(
  transaction?: Transaction
): transaction is Transaction & { transactionKind: 'payment' } {
  return transaction?.transactionKind === 'payment'
}

export function transactionIsRefund(
  transaction?: Transaction
): transaction is Transaction & { transactionKind: 'refund' } {
  return transaction?.transactionKind === 'refund'
}

export function assertTransactionIsCharge(
  transaction?: Transaction
): asserts transaction is Transaction & { transactionKind: 'charge' } {
  if (!transactionIsCharge(transaction)) {
    throw new Error('Transaction is not a charge')
  }
}

export function assertTransactionIsPayment(
  transaction?: Transaction
): asserts transaction is Transaction & { transactionKind: 'payment' } {
  if (!transactionIsPayment(transaction)) {
    throw new Error('Transaction is not a payment')
  }
}

export function assertTransactionIsRefund(
  transaction?: Transaction
): asserts transaction is Transaction & { transactionKind: 'refund' } {
  if (!transactionIsRefund(transaction)) {
    throw new Error('Transaction is not a refund')
  }
}
